import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useCoEdit } from '@/lib/useCoEdit';
import { emitCoachEvent } from '@/coach/coachApi';
import { CoEditBar, CoEditCursors } from '@/components/dashboards/CoEditPresence';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription
} from '@/components/ui/dialog';
import { WidgetPreview } from '@/components/dashboards/WidgetPreview';
import { InvestigatePanel } from '@/components/investigate/InvestigatePanel';
import { PublishDialog } from '@/components/dashboards/PublishDialog';
import ExportFrameworkDialog from '@/components/ExportFrameworkDialog';
import { PropertiesPanel } from '@/components/dashboards/PropertiesPanel';
import { WIDGET_TYPES, SAMPLE_DATA, COLORS } from '@/components/dashboards/widgetShared';
import {
  BarChart3, LineChart as LineChartIcon, PieChart as PieChartIcon,
  AreaChart as AreaChartIcon, Table, Hash, Plus, Save, Eye, ArrowLeft,
  GripVertical, Trash2, Settings2, Move, LayoutGrid, X, Download
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip,
  ResponsiveContainer
} from 'recharts';
import { toast } from 'sonner';
import { genId } from '@/components/report-builder/genId';
import { PageNavChrome, PagesManager, isPageVisible, normalizeNavStyle } from '@/components/dashboards/PageNavChrome';

export default function DashboardBuilderPage() {
  const navigate = useNavigate();
  const { dashId } = useParams();
  const [dashName, setDashName] = useState('');
  const [dashDesc, setDashDesc] = useState('');
  const [widgets, setWidgets] = useState([]);
  const [selectedWidget, setSelectedWidget] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [newWidgetType, setNewWidgetType] = useState('kpi');
  const [newWidgetTitle, setNewWidgetTitle] = useState('');
  const [saving, setSaving] = useState(false);
  const [existingDashboards, setExistingDashboards] = useState([]);
  const [loadDashId, setLoadDashId] = useState(null);
  // Iter D — tab support. `tabs` is the list of tab metadata; when
  // empty the dashboard is a single-canvas (default) doc.
  const [tabs, setTabs] = useState([]);
  const [activeTabId, setActiveTabId] = useState(null);
  // Pages — author-chosen navigation style + page-manager dialog.
  const [navStyle, setNavStyle] = useState('top_tabs');
  const [pagesOpen, setPagesOpen] = useState(false);
  const [gridSnap, setGridSnap] = useState(1);
  // Iter-149 — Publish-to-Embed flow state.
  const [publishOpen, setPublishOpen] = useState(false);
  const [publishExpiryHours, setPublishExpiryHours] = useState(168);
  const [publishToken, setPublishToken] = useState(null);
  const [publishBusy, setPublishBusy] = useState(false);
  const [investigateOpen, setInvestigateOpen] = useState(false);
  const [investigateTarget, setInvestigateTarget] = useState(null);
  // Export — same rich dialog as the dashboard viewer. Operates on the saved
  // dashboard (by id) so the server render matches the viewer exactly.
  const [exportOpen, setExportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState({ current: 0, total: 0, label: '' });
  const exportAbortRef = React.useRef(null);
  // Iter-149 — Cross-filter receiver payload. When a widget marked
  // `cross_filter_source` publishes a filter, we set it here and every
  // receiver widget re-derives its displayed data.
  const [crossFilterPayload, setCrossFilterPayload] = useState([]);
  useEffect(() => {
    const handler = (e) => setCrossFilterPayload(e.detail?.filters || []);
    window.addEventListener('inzara.crossfilter', handler);
    return () => window.removeEventListener('inzara.crossfilter', handler);
  }, []);

  useEffect(() => {
    api.getDashboards().then(setExistingDashboards).catch(console.error);
  }, []);

  // Autonomous Coach — signal that the dashboard builder is open (trusted event,
  // only forwarded when an autonomous plan session is active).
  useEffect(() => { emitCoachEvent('dashboard_builder_opened', 'dashboard-add-widget'); }, []);

  // ── Phase 3: self-hosted co-editing (only for saved dashboards) ──
  const { user } = useAuth();
  const me = user?.user_id;
  const room = loadDashId ? `dash:${loadDashId}` : null;
  const applySnapshot = useCallback((s) => {
    if (s.widgets) setWidgets(s.widgets);
    if (s.dashName !== undefined) setDashName(s.dashName);
    if (s.dashDesc !== undefined) setDashDesc(s.dashDesc);
    if (s.tabs) setTabs(s.tabs);
    if (s.navStyle) setNavStyle(s.navStyle);
  }, []);
  const co = useCoEdit({ room, me, snapshot: { widgets, dashName, dashDesc, tabs, navStyle }, applySnapshot });
  const amEditor = co.amEditor;
  const guardEdit = () => {
    if (!amEditor) { toast.error('Ask for edit control first'); return false; }
    return true;
  };

  // ── Pages: add / rename / reorder / duplicate / delete / hide ──
  // A "page" is a DashboardTab; widgets belong to a page via widget.tab_id.
  const addPage = () => {
    if (!guardEdit()) return;
    setTabs(prev => {
      const tab_id = genId('tab_');
      const next = [...prev, { tab_id, name: `Page ${prev.length + 1}`, order: prev.length, visible: true }];
      setActiveTabId(tab_id);
      return next;
    });
  };
  const renamePage = (tab_id, name) => { if (!amEditor) return; setTabs(prev => prev.map(t => t.tab_id === tab_id ? { ...t, name } : t)); };
  const togglePageHidden = (tab_id) => { if (!guardEdit()) return; setTabs(prev => prev.map(t => t.tab_id === tab_id ? { ...t, visible: isPageVisible(t) ? false : true } : t)); };
  const reorderPages = (from, to) => {
    if (!guardEdit()) return;
    setTabs(prev => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next.map((t, i) => ({ ...t, order: i }));
    });
  };
  const duplicatePage = (tab_id) => {
    if (!guardEdit()) return;
    const src = tabs.find(t => t.tab_id === tab_id);
    if (!src) return;
    const newId = genId('tab_');
    const firstId = tabs[0]?.tab_id;
    const pageWidgets = widgets.filter(w => (w.tab_id || firstId) === tab_id);
    const copies = pageWidgets.map(w => ({ ...w, widget_id: genId('w_'), tab_id: newId }));
    setTabs(prev => {
      const idx = prev.findIndex(t => t.tab_id === tab_id);
      const next = [...prev];
      next.splice(idx + 1, 0, { tab_id: newId, name: `${src.name} (Copy)`, order: idx + 1, visible: isPageVisible(src) });
      return next.map((t, i) => ({ ...t, order: i }));
    });
    setWidgets(prev => [...prev, ...copies]);
    setActiveTabId(newId);
  };
  const deletePage = (tab_id) => {
    if (!guardEdit()) return;
    if (tabs.length <= 1) { toast.error('A dashboard needs at least one page'); return; }
    const firstId = tabs[0]?.tab_id;
    // Deleting a page deletes the widgets on it.
    setWidgets(prev => prev.filter(w => (w.tab_id || firstId) !== tab_id));
    setTabs(prev => {
      const next = prev.filter(t => t.tab_id !== tab_id).map((t, i) => ({ ...t, order: i }));
      if (activeTabId === tab_id) setActiveTabId(next[0]?.tab_id ?? null);
      return next;
    });
  };

  const addWidget = () => {
    if (!guardEdit()) return;
    const wType = WIDGET_TYPES.find(w => w.type === newWidgetType);
    const w = {
      widget_id: genId('w_'),
      widget_type: newWidgetType,
      title: newWidgetTitle || `${wType?.label || 'Widget'}`,
      data_key: '',
      config: {},
      tab_id: activeTabId || (tabs[0]?.tab_id ?? null),
      x: 0,
      y: widgets.length * 4,
      w: wType?.defaultW || 4,
      h: wType?.defaultH || 3,
    };
    setWidgets(prev => [...prev, w]);
    setSelectedWidget(w.widget_id);
    setNewWidgetTitle('');
    setAddOpen(false);
    toast.success('Widget added');
    emitCoachEvent('dashboard_widget_added', 'dashboard-add-widget');
  };

  const removeWidget = (id) => {
    if (!guardEdit()) return;
    setWidgets(prev => prev.filter(w => w.widget_id !== id));
    if (selectedWidget === id) setSelectedWidget(null);
    toast.success('Widget removed');
  };

  const saveDashboard = async () => {
    if (!guardEdit()) return;
    if (!dashName.trim()) {
      toast.error('Dashboard name required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: dashName, description: dashDesc, widgets,
        tabs, grid_snap: Math.max(1, Number(gridSnap) || 1),
        nav_style: normalizeNavStyle(navStyle),
      };
      if (loadDashId) {
        await api.updateDashboard(loadDashId, payload);
        toast.success('Dashboard updated');
      } else {
        const result = await api.createDashboard(payload);
        setLoadDashId(result.dashboard_id);
        toast.success('Dashboard saved');
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const loadDashboard = async (dashId) => {
    try {
      const dash = await api.getDashboard(dashId);
      setDashName(dash.name);
      setDashDesc(dash.description || '');
      setWidgets(dash.widgets || []);
      setTabs(dash.tabs || []);
      setActiveTabId((dash.tabs || []).find(isPageVisible)?.tab_id ?? (dash.tabs || [])[0]?.tab_id ?? null);
      setNavStyle(normalizeNavStyle(dash.nav_style));
      setGridSnap(dash.grid_snap || 1);
      setLoadDashId(dashId);
      toast.success('Dashboard loaded');
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Load an existing dashboard when opened via /dashboards/create/:dashId
  // (e.g. after AI generates a dashboard from an uploaded file). When there is
  // no dashId this is a NEW dashboard — reset to a blank canvas so a previous
  // session's unsaved work never carries over.
  useEffect(() => {
    if (dashId) {
      loadDashboard(dashId);
    } else {
      setDashName('');
      setDashDesc('');
      setWidgets([]);
      setTabs([]);
      setActiveTabId(null);
      setNavStyle('top_tabs');
      setGridSnap(1);
      setSelectedWidget(null);
      setLoadDashId(null);
    }
  }, [dashId]);

  const selected = widgets.find(w => w.widget_id === selectedWidget);
  // Widgets visible on the current tab. When no tabs are configured
  // (tabs.length === 0) we show every widget regardless of tab_id so
  // legacy dashboards still render.
  const visibleWidgets = tabs.length === 0 ? widgets : widgets.filter(w => (w.tab_id || tabs[0]?.tab_id) === activeTabId);

  // Iter-149 — Publish-to-Embed one-click flow. Generates an embed
  // token via the existing /api/embed/generate endpoint and hands the
  // caller both an <iframe> snippet and an <InzaraDashboard/> snippet.
  const publish = async () => {
    if (!loadDashId) {
      toast.error('Save the dashboard first, then publish.');
      return;
    }
    setPublishBusy(true);
    setPublishToken(null);
    try {
      const res = await api.generateEmbedToken({
        resource_type: 'dashboard',
        resource_id: loadDashId,
        label: `Publish: ${dashName || 'Untitled'}`,
        permissions: ['read'],
        filters: [],
        expires_hours: publishExpiryHours,
      });
      setPublishToken(res);
    } catch (err) { toast.error(err.message || 'Publish failed'); }
    finally { setPublishBusy(false); }
  };
  const copyToClipboard = (txt) => {
    try {
      navigator.clipboard.writeText(txt).then(
        () => toast.success('Copied to clipboard'),
        () => toast.error('Copy failed — select and copy manually')
      );
    } catch (e) { toast.error('Copy failed'); }
  };

  const buildExportContext = useCallback(() => {
    const pages = (tabs || []).map(t => ({ id: t.tab_id, name: t.name }));
    const sel = '[data-testid="builder-canvas"]';
    return {
      title: dashName || 'Dashboard',
      defaultFilename: (dashName || 'dashboard'),
      pages,
      themeColor: null,
      pageSelector: sel,
      // One capturable "visual" = the on-screen dashboard grid, so PNG and the
      // image-based formats capture exactly what's shown on the canvas.
      visuals: [{ id: 'dashboard', title: dashName || 'Dashboard', type: 'dashboard', selector: sel, columns: [], rows: [] }],
    };
  }, [tabs, dashName]);

  // Client-side PDF of the on-screen dashboard grid (the builder renders the
  // canvas; the reports server-PDF endpoint is report-only). Mirrors the
  // viewer's in-browser capture path so output matches what's shown.
  const exportCanvasPdf = async (_pageIds = null, _opts = {}) => {
    const el = document.querySelector('[data-testid="builder-canvas"]') || co.canvasRef?.current;
    if (!el) { toast.error('Nothing on the canvas to export'); throw new Error('no canvas'); }
    setExporting(true);
    setExportProgress({ current: 0, total: 1, label: 'Rendering dashboard…' });
    try {
      const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
        import('html2canvas'), import('jspdf'),
      ]);
      const canvas = await html2canvas(el, { backgroundColor: '#0b0b0f', scale: 2, logging: false, useCORS: true, imageTimeout: 15000 });
      const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
      const pw = pdf.internal.pageSize.getWidth();
      const ph = pdf.internal.pageSize.getHeight();
      const ratio = Math.min(pw / canvas.width, ph / canvas.height);
      const w = canvas.width * ratio;
      const h = canvas.height * ratio;
      pdf.addImage(canvas.toDataURL('image/jpeg', 0.92), 'JPEG', (pw - w) / 2, 20, w, h);
      const base = (_opts.filename || dashName || 'dashboard').replace(/[^\w.-]/g, '_');
      pdf.save(`${base}.pdf`);
      toast.success('Exported PDF');
    } catch (e) {
      throw e;
    } finally {
      setExporting(false);
      setExportProgress({ current: 0, total: 0, label: '' });
    }
  };

  const cancelExport = () => { try { exportAbortRef.current?.abort(); } catch { /* no-op */ } };

  return (
    <div className="flex h-full" data-testid="dashboard-builder-page">
      {/* Left: Widget Palette & Config */}
      <div className="w-72 border-r border-border/50 flex flex-col overflow-hidden" style={{ background: 'var(--glass-bg)', backdropFilter: 'blur(12px)' }}>
        <div className="p-4 border-b border-border/50">
          <div className="flex items-center gap-2 mb-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} className="rounded-lg w-8 h-8" data-testid="builder-back-btn">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <h2 className="text-sm font-heading font-bold text-foreground">Dashboard Builder</h2>
          </div>
          <Input
            data-testid="dashboard-name-input"
            placeholder="Dashboard name"
            value={dashName}
            onChange={e => setDashName(e.target.value)}
            disabled={!amEditor}
            className="rounded-lg bg-muted/50 border-border/50 text-sm mb-2"
          />
          <Input
            placeholder="Description (optional)"
            value={dashDesc}
            onChange={e => setDashDesc(e.target.value)}
            disabled={!amEditor}
            className="rounded-lg bg-muted/50 border-border/50 text-xs"
          />
        </div>

        {/* Load existing */}
        {existingDashboards.length > 0 && (
          <div className="p-3 border-b border-border/50">
            <Label className="text-[10px] text-muted-foreground uppercase tracking-wider mb-2 block">Load Existing</Label>
            <Select onValueChange={loadDashboard}>
              <SelectTrigger className="rounded-lg bg-muted/50 border-border/50 text-xs h-8">
                <SelectValue placeholder="Select dashboard" />
              </SelectTrigger>
              <SelectContent>
                {existingDashboards.map(d => (
                  <SelectItem key={d.dashboard_id} value={d.dashboard_id} className="text-xs">{d.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Widget palette */}
        <div className="p-3 border-b border-border/50" data-coach-target="dashboard-add-widget">
          <Label className="text-[10px] text-muted-foreground uppercase tracking-wider mb-2 block">Add Widget</Label>
          <div className="grid grid-cols-3 gap-1.5">
            {WIDGET_TYPES.map(wt => (
              <button
                key={wt.type}
                onClick={() => { if (!guardEdit()) return; setNewWidgetType(wt.type); setAddOpen(true); emitCoachEvent('dashboard_widget_type_selected', 'dashboard-add-widget'); }}
                disabled={!amEditor}
                className="flex flex-col items-center gap-1 p-2 rounded-lg bg-muted/30 hover:bg-primary/10 border border-transparent hover:border-primary/20 transition-all text-muted-foreground hover:text-primary active:scale-95 disabled:opacity-40 disabled:pointer-events-none"
                data-testid={`add-${wt.type}-btn`}
              >
                <wt.icon className="w-4 h-4" strokeWidth={1.5} />
                <span className="text-[9px]">{wt.label}</span>
              </button>
            ))}
          </div>
        </div>

        <PropertiesPanel selected={selected} widgets={widgets} setWidgets={amEditor ? setWidgets : () => {}} />

        {/* Actions */}
        <div className="p-3 border-t border-border/50 space-y-2">
          <Button
            data-testid="save-dashboard-btn"
            onClick={saveDashboard}
            disabled={saving || !dashName.trim() || !amEditor}
            className="w-full rounded-lg bg-primary text-primary-foreground text-xs active:scale-95 transition-transform"
          >
            <Save className="w-3.5 h-3.5 mr-1.5" /> {saving ? 'Saving...' : 'Save Dashboard'}
          </Button>
        </div>
      </div>

      {/* Right: Canvas */}
      <div className="flex-1 overflow-auto p-6 bg-background">
        <div className="max-w-6xl mx-auto relative" ref={co.canvasRef} onMouseMove={co.onCanvasMouseMove}>
          <CoEditCursors cursors={co.cursors} />
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-heading font-bold text-foreground tracking-tight">
                {dashName || 'Untitled Dashboard'}
              </h1>
              {dashDesc && <p className="text-xs text-muted-foreground mt-1">{dashDesc}</p>}
            </div>
            <div className="flex items-center gap-3">
              <CoEditBar members={co.members} editor={co.editor} amEditor={amEditor} me={me} onRequest={co.requestControl} onTake={co.takeControl} />
              {crossFilterPayload.length > 0 && (
                <button
                  onClick={() => setCrossFilterPayload([])}
                  className="text-[10px] px-2 py-1 rounded-full bg-primary/15 text-primary border border-primary/30 hover:bg-primary/25 flex items-center gap-1"
                  data-testid="clear-crossfilter-chip"
                  title="Clear cross-filter"
                >📌 {crossFilterPayload.map(f => `${f.field}=${f.value}`).join(', ')} ×</button>
              )}
              <div className="flex items-center gap-1.5">
                <Label className="text-[10px] text-muted-foreground uppercase tracking-wider">Snap</Label>
                <Select value={String(gridSnap)} onValueChange={v => setGridSnap(Number(v))}>
                  <SelectTrigger className="h-7 w-16 text-[10px]" data-testid="grid-snap-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1" className="text-xs">1</SelectItem>
                    <SelectItem value="2" className="text-xs">2</SelectItem>
                    <SelectItem value="4" className="text-xs">4</SelectItem>
                    <SelectItem value="6" className="text-xs">6</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Badge variant="secondary" className="text-xs">{visibleWidgets.length}/{widgets.length} widgets</Badge>
              <Button size="sm" variant="outline" onClick={() => { if (!amEditor) { toast.error('Ask for edit control first'); return; } if (tabs.length === 0) { const tab_id = genId('tab_'); setTabs([{ tab_id, name: 'Page 1', order: 0, visible: true }]); setActiveTabId(tab_id); } setPagesOpen(true); }} disabled={!amEditor} className="h-7 text-[10px]" data-testid="manage-pages-btn" title="Manage pages & navigation">
                <LayoutGrid className="w-3 h-3 mr-1" /> Pages{tabs.length > 1 ? ` (${tabs.length})` : ''}
              </Button>
              <Button size="sm" variant="outline" onClick={() => { if (!loadDashId) { toast.error('Save the dashboard first, then export.'); return; } setExportOpen(true); }} disabled={!loadDashId} className="h-7 text-[10px]" data-testid="dashboard-export-btn" title={loadDashId ? 'Export this dashboard' : 'Save first, then export'}>
                <Download className="w-3 h-3 mr-1" /> Export
              </Button>
              <Button size="sm" variant="outline" onClick={() => setPublishOpen(true)} disabled={!loadDashId} className="h-7 text-[10px]" data-testid="publish-btn" title={loadDashId ? 'Publish to a shareable embed' : 'Save first, then publish'}>
                🚀 Publish
              </Button>
            </div>
          </div>

          {/* Pages — live navigation preview (author-chosen style) */}
          <PageNavChrome
            style={navStyle}
            pages={tabs}
            activeId={activeTabId}
            onChange={setActiveTabId}
            showHidden
          >
          {visibleWidgets.length === 0 ? (
            <div className="glass-card rounded-2xl p-16 flex flex-col items-center justify-center text-center">
              <div className="p-4 rounded-2xl bg-primary/10 border border-primary/20 mb-4">
                <Plus className="w-8 h-8 text-primary" strokeWidth={1.5} />
              </div>
              <h3 className="text-base font-heading font-semibold text-foreground mb-1">Start Building</h3>
              <p className="text-xs text-muted-foreground max-w-sm">
                {tabs.length > 0 ? 'This page is empty. Add widgets from the palette on the left.' : 'Add widgets from the palette on the left. Drag to rearrange, click to configure.'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="builder-canvas">
              {visibleWidgets.map(widget => (
                <div
                  key={widget.widget_id}
                  onContextMenu={(e) => {
                    // Right-click any dashboard widget opens Investigate
                    // Why prefilled with the widget's own measure and
                    // whatever cross-filters are currently in effect.
                    e.preventDefault();
                    const measures = widget.config?.value_measures || widget.value_measures || [];
                    setInvestigateTarget({
                      metric: measures[0],
                      model_id: widget.config?.model_id || widget.model_id,
                      window: 'the current view',
                      source: 'dashboard',
                      widget_id: widget.widget_id,
                      widget_name: widget.name,
                      filters: crossFilterPayload || [],
                    });
                    setInvestigateOpen(true);
                  }}
                >
                  <WidgetPreview
                    widget={widget}
                    compareWidget={widget.config?.compare_with ? widgets.find(w => w.widget_id === widget.config.compare_with) : null}
                    crossFilters={crossFilterPayload}
                    isSelected={selectedWidget === widget.widget_id}
                    onSelect={setSelectedWidget}
                    onRemove={removeWidget}
                  />
                </div>
              ))}
            </div>
          )}
          </PageNavChrome>
        </div>
      </div>

      <PublishDialog
        open={publishOpen}
        onOpenChange={setPublishOpen}
        loadDashId={loadDashId}
        expiryHours={publishExpiryHours}
        onExpiryChange={setPublishExpiryHours}
        publishToken={publishToken}
        busy={publishBusy}
        onGenerate={publish}
        onCopy={copyToClipboard}
      />

      <ExportFrameworkDialog
        open={exportOpen}
        onOpenChange={setExportOpen}
        mode="dashboard"
        resourceId={loadDashId}
        resourceName={dashName || 'Dashboard'}
        resourceType="dashboard"
        getContext={buildExportContext}
        nativeReportPdf={false}
        onNativePdf={exportCanvasPdf}
        exporting={exporting}
        exportProgress={exportProgress}
        onCancelExport={cancelExport}
      />

      {/* Add Widget Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-foreground text-base">Add Widget</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-xs text-muted-foreground">Widget Title</Label>
              <Input
                data-testid="new-widget-title"
                value={newWidgetTitle}
                onChange={e => setNewWidgetTitle(e.target.value)}
                placeholder={WIDGET_TYPES.find(w => w.type === newWidgetType)?.label || 'Widget'}
                className="rounded-lg bg-muted/50 border-border/50 text-sm mt-1"
              />
            </div>
            <Button
              data-testid="confirm-add-widget"
              onClick={addWidget}
              className="w-full rounded-lg bg-primary text-primary-foreground text-xs active:scale-95"
            >
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Add to Dashboard
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <InvestigatePanel open={investigateOpen} onOpenChange={setInvestigateOpen} target={investigateTarget} />

      {/* Pages manager — author-only page management + navigation style */}
      <Dialog open={pagesOpen} onOpenChange={setPagesOpen}>
        <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-md" data-testid="pages-manager-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-foreground text-base">Pages & Navigation</DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">Add, rename, reorder, duplicate, hide pages and pick how viewers move between them.</DialogDescription>
          </DialogHeader>
          <div className="mt-2 max-h-[60vh] overflow-y-auto">
            <PagesManager
              pages={tabs}
              activeId={activeTabId}
              navStyle={navStyle}
              onSelect={setActiveTabId}
              onNavStyle={setNavStyle}
              onAdd={addPage}
              onRename={renamePage}
              onDuplicate={duplicatePage}
              onDelete={deletePage}
              onToggleHidden={togglePageHidden}
              onReorder={reorderPages}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
